import {InstallationEquipment} from "../../models/operations/InstallationEquipment";
import {ProcessStep} from "../../models/ProcessStep";
import {EElementType, elementsNameKey} from "../../models/utils/EElementType";
import {AElement} from "../../models/elements/AElement";
import IdentificationStep from "../../components/steps/identification/IdentificationStep";
import GPSCoordinates from "../../models/GPSCoordinates";
import GeolocationStep from "../../components/steps/geolocation/GeolocationStep";
import CommentaryStep from "../../components/steps/commentary/CommentaryStep";
import {AOperation} from "../../models/operations/AOperation";
import {EOperationType} from "../../models/utils/EOperationType";
import {InstallationElement} from "../../models/operations/InstallationElement";
import EquipmentInstallationSteps from "./operations/EquipmentInstallationSteps";
import ElementInstallationSteps from "./operations/ElementInstallationSteps";
import EquipmentUninstallationSteps from "./operations/EquipmentUninstallationSteps";
import UninstallEquipment from "../../models/operations/UninstallEquipment";
import ElementUninstallationSteps from "./operations/ElementUninstallationSteps";
import {UninstallElement} from "../../models/operations/UninstallElement";
import ElementReplacementSteps from "./operations/ElementReplacementSteps";
import {ReplaceElement} from "../../models/operations/ReplaceElement";
import GeolocationSteps from "./operations/GeolocationSteps";
import {Geolocate} from "../../models/operations/Geolocate";

/**
 *
 * @author Alizée Grand
 * @version 1.0
 * @creation 09/05/2022
 * @last_update 09/05/2022
 **/
class StepsAccordingToSpecificationsService {

    public getOperationSteps(operation: AOperation, setOperation: (operation: AOperation) => void): ProcessStep[] {
        let steps = this.generatesBasicOperationSteps(setOperation, operation);
        switch (operation.type) {
            case EOperationType.InstallationEquipment:
                steps =  EquipmentInstallationSteps.getEquipmentInstallationSteps(operation as InstallationEquipment, setOperation).concat(steps);
                break;
            case EOperationType.InstallationElement:
                steps = ElementInstallationSteps.getElementInstallationSteps(operation as InstallationElement, setOperation).concat(steps);
                break;
            case EOperationType.UninstallationEquipment:
                steps = EquipmentUninstallationSteps.getEquipmentUninstallationSteps(operation as UninstallEquipment, setOperation).concat(steps);
                break;
            case EOperationType.UninstallationElement:
                steps = ElementUninstallationSteps.getElementUninstallationSteps(operation as UninstallElement, setOperation).concat(steps);
                break;
            case EOperationType.ReplacementElement:
                steps = ElementReplacementSteps.getElementReplacementSteps(operation as ReplaceElement, setOperation).concat(steps);
                break;
            case EOperationType.Geolocate:
                steps = GeolocationSteps.getGeolocationSteps(operation as Geolocate, setOperation).concat(steps);
                break;
            default:
                // Nothing to do
                break;
        }
        return steps;
    }

    public generatesBasicOperationSteps(setOperation: (operation: AOperation) => void, operation: AOperation): ProcessStep[] {
        let steps: ProcessStep[] = [];
        steps.push(this.getGeolocationStep(setOperation, operation));
        steps.push(this.getCommentaryStep(setOperation, operation));
        return steps;
    }

    // Identification

    public getIdentificationStep(elementType: EElementType, setElement: (element: AElement) => void, element: AElement, stepTitle?: string): ProcessStep {
        const title = "steps.titles.identification";
        const component = this.getIdentificationComponent(elementType, setElement, element, stepTitle);
        return new ProcessStep(title, component);
    }

    private getIdentificationComponent(type: EElementType, setElement: (element: AElement) => void, element?: AElement, stepTitle?: string): JSX.Element {
        let random = Math.random();
        return <IdentificationStep key={random} elementType={type} initialElement={element} onValidate={setElement} detailTitle={stepTitle}/>;
    }

    // Geolocation

    private getGeolocationStep(setOperation: (operation: AOperation) => void, operation: AOperation): ProcessStep {
        const setLocation = (location: GPSCoordinates) => {
            operation.geolocation = location;
            setOperation(operation);
        };
        return new ProcessStep("steps.titles.localisation", this.getGeolocationComponent(setLocation, operation.geolocation));
    }

    private getGeolocationComponent(setLocation : (location: GPSCoordinates) => void, geolocation?: GPSCoordinates): JSX.Element {
        return <GeolocationStep onValidate={setLocation} currentLocation={geolocation}/>;
    }

    // Commentary

    private getCommentaryStep(setOperation: (operation: AOperation) => void, operation: AOperation): ProcessStep {
        const setComment = (commentary: string) => {
            operation.commentary = commentary;
            setOperation(operation);
        };
        return new ProcessStep("steps.titles.comment", this.getCommentaryComponent(setComment, operation.commentary));
    }

    private getCommentaryComponent(setComment : (commentary: string) => void, comment?: string) {
        return <CommentaryStep onValidate={setComment} currentCommentary={comment}/>;
    }
}

export default new StepsAccordingToSpecificationsService();
